import { useMutation, useQuery } from "@tanstack/react-query";
import { sessionApiService, ScanSampleCollectionApiService, ScanSampleSeperationApiService } from "modules/Session/Session/api/session.api";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statusCode: number;
}

export const useSessionService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => sessionApiService.insertData(payload),
  });

  const SelectQuery = useQuery<ApiResponse, Error>({
    queryKey: ["session-select", payload?.sessionid],
    queryFn: () => sessionApiService.selectData({ sessionid: payload?.sessionid }),
    enabled: !!payload?.sessionid,
  });

  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: ["session-select-all", payload?.searchtext, payload?.currentpage, payload?.pagesize, payload?.studyid],
    queryFn: () => sessionApiService.selectAllData(payload),
    enabled: payload?.enabled !== undefined ? payload.enabled : !!payload?.studyid,
  });

  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => sessionApiService.updateData(payload),
  });

  const GlobalReportMutation = useMutation<any, Error, { studyid: number }>({
    mutationFn: (payload: { studyid: number }) => sessionApiService.globalReportSelectAllData(payload),
  });

  return { InsertMutation, SelectQuery, SelectAllQuery, UpdateMutation, GlobalReportMutation };
};

export const useSessionProgressService = (payload?: any) => {
  const SessionProgressQuery = useQuery<ApiResponse, Error>({
    queryKey: ["session-progress", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize, payload?.tabstatus],
    queryFn: () => sessionApiService.scanProgressData(payload),
    enabled: !!payload?.sessionid,
  });

  return { SessionProgressQuery };
};

export const useSessionDashboardService = (payload?: any) => {
  const SessionDashboardQuery = useQuery<ApiResponse, Error>({
    queryKey: ["session-dashboard", payload?.studyid, payload?.periodid],
    queryFn: () => sessionApiService.sessionDashboardData(payload),
    enabled: !!payload?.studyid,
  });

  return { SessionDashboardQuery };
};

export const useScanSampleCollectionService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleCollectionApiService.insertData(payload),
  });

  const ValidateSubjectMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleCollectionApiService.validateSubjectBarcode(payload),
  });

  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: ["scan-sample-collection-select-all", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize],
    queryFn: () => ScanSampleCollectionApiService.selectAllData(payload),
    enabled: !!payload?.sessionid,
  });

  return { InsertMutation, ValidateSubjectMutation, SelectAllQuery };
};

export const useScanSampleSeperationService = (payload?: any) => {
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleSeperationApiService.insertData(payload),
  });

  const ValidateVaccutainerMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => ScanSampleSeperationApiService.validateVaccutainerBarcode(payload),
  });

  const SelectAllQuery = useQuery<ApiResponse, Error>({
    queryKey: ["scan-sample-seperation-select-all", payload?.sessionid, payload?.searchtext, payload?.currentpage, payload?.pagesize],
    queryFn: () => ScanSampleSeperationApiService.selectAllData(payload),
    enabled: !!payload?.sessionid,
  });

  return { InsertMutation, ValidateVaccutainerMutation, SelectAllQuery };
};

export const useScanSampleCollectionReportService = () => {
  const CollectionSubjectReportMutation = useMutation<any, Error, any>({
    mutationFn: (payload: any) =>
      ScanSampleCollectionApiService.collectionSubjectReportSelectAllData(payload),
  });

  const CollectionSummaryReportMutation = useMutation<any, Error, any>({
    mutationFn: (payload: any) =>
      ScanSampleCollectionApiService.collectionSummaryReportSelectAllData(payload),
  });

  return { CollectionSubjectReportMutation, CollectionSummaryReportMutation };
};

export const useScanSampleSeperationReportService = () => {
  const SeperationSubjectReportMutation = useMutation<any, Error, any>({
    mutationFn: (payload: any) =>
      ScanSampleSeperationApiService.seperationSubjectReportSelectAllData(payload),
  });

  const SeperationSummaryReportMutation = useMutation<any, Error, any>({
    mutationFn: (payload: any) =>
      ScanSampleSeperationApiService.seperationSummaryReportSelectAllData(payload),
  });

  return { SeperationSubjectReportMutation, SeperationSummaryReportMutation };
};
